import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTicketAlt,
  faCommentDots,
  faLock,
  faMobileAlt,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { validateInappropriateLanguage } from '../../../utils/profanitySanitizer';

/**
 * Modal amplio con editor estructurado, sanitización de lenguaje y vista previa en vivo de WhatsApp siempre visible
 */
const TagCompleteModal = ({
  itemToComplete,
  isCompleting,
  isDark,
  onClose,
  onConfirm
}) => {
  const [resolutionMessage, setResolutionMessage] = useState('');

  const prefixText = 'Hola, respecto a tu reporte de soporte técnico te informamos lo siguiente:';
  const suffixText = 'Agradecemos tu paciencia y quedamos a tu disposición si tienes alguna duda adicional.';

  useEffect(() => {
    if (itemToComplete) {
      setResolutionMessage(
        `La incidencia "${itemToComplete.vNombre}" ha sido resuelta con éxito. Tu ticket ha sido concluido.`
      );
    }
  }, [itemToComplete]);

  // Validación y sanitización de lenguaje inapropiado / groserías
  const profanityValidation = useMemo(() => {
    return validateInappropriateLanguage(resolutionMessage);
  }, [resolutionMessage]);

  if (!itemToComplete) return null;

  const now = new Date();
  const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const totalTickets = itemToComplete.iTotalTickets || 0;

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.68)', zIndex: 1060 }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '890px', width: '94%' }}>
        <div
          className="modal-content border shadow-lg"
          style={{
            backgroundColor: isDark ? '#14202e' : '#ffffff',
            borderColor: isDark ? '#233142' : '#cbd5e1',
            borderRadius: '16px'
          }}
        >
          {/* Cabecera del modal */}
          <div className="modal-header border-bottom px-4 py-3 d-flex align-items-center justify-content-between" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
            <div className="d-flex align-items-center gap-2.5">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-success"
                style={{
                  width: '38px',
                  height: '38px',
                  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5'
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '1.15rem' }} />
              </div>
              <div>
                <h6 className="fw-bold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a', fontSize: '1rem' }}>
                  Resolver Incidencia
                </h6>
                <div className="small fw-semibold text-primary" style={{ fontSize: '0.86rem' }}>
                  "{itemToComplete.vNombre}"
                </div>
              </div>
            </div>

            <div
              className="d-flex align-items-center gap-2 px-3 py-1.5 rounded-pill border"
              style={{
                backgroundColor: isDark ? '#0f172a' : '#f8fafc',
                borderColor: isDark ? '#233142' : '#e2e8f0'
              }}
            >
              <FontAwesomeIcon icon={faTicketAlt} className="text-primary" style={{ fontSize: '0.85rem' }} />
              <span className="small fw-bold" style={{ color: isDark ? '#f9fafd' : '#0f172a', fontSize: '0.82rem' }}>
                {totalTickets} ticket{totalTickets === 1 ? '' : 's'} vinculados
              </span>
            </div>
          </div>

          {/* Cuerpo principal en 2 columnas */}
          <div className="modal-body px-4 py-3">
            <div className="row g-3">
              {/* COLUMNA IZQUIERDA: EDITOR ESTRUCTURADO */}
              <div className="col-12 col-md-6 d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="small fw-bold d-flex align-items-center gap-1.5" style={{ color: isDark ? '#f1f5f9' : '#1e293b', fontSize: '0.84rem' }}>
                    <FontAwesomeIcon icon={faCommentDots} className="text-primary" />
                    <span>Redactar Notificación</span>
                  </span>
                  <span className="text-muted" style={{ fontSize: '0.70rem' }}>
                    Plantilla Meta Utilidad
                  </span>
                </div>

                <div className="d-flex flex-column gap-2 flex-grow-1">
                  {/* Prefijo fijo */}
                  <div
                    className="p-2.5 rounded-2 border"
                    style={{
                      backgroundColor: isDark ? '#0b141a' : '#f8fafc',
                      borderColor: isDark ? '#1f2e3d' : '#e2e8f0',
                      fontSize: '0.78rem'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="text-muted fw-semibold" style={{ fontSize: '0.66rem', letterSpacing: '0.3px' }}>
                        <FontAwesomeIcon icon={faLock} className="me-1" />
                        PREFIJO INSTITUCIONAL (FIJO)
                      </span>
                    </div>
                    <div className="fw-medium" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                      {prefixText}
                    </div>
                  </div>

                  {/* Alerta de lenguaje inapropiado */}
                  {!profanityValidation.isValid && (
                    <div
                      className="alert alert-danger d-flex align-items-center gap-2 p-2 rounded-2 border mb-1"
                      style={{
                        fontSize: '0.74rem',
                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                        borderColor: isDark ? 'rgba(239, 68, 68, 0.35)' : '#fecaca',
                        color: isDark ? '#fca5a5' : '#b91c1c'
                      }}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} className="flex-shrink-0" />
                      <span>
                        <strong>Lenguaje no permitido:</strong> Se detectó el término <code>"{profanityValidation.foundWord}"</code>. No se permite enviar palabras obscenas a los clientes.
                      </span>
                    </div>
                  )}

                  {/* Cuerpo editable */}
                  <div className="flex-grow-1 d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="small fw-bold" style={{ color: isDark ? '#38bdf8' : '#0284c7', fontSize: '0.75rem' }}>
                        Detalle de la resolución (editable):
                      </span>
                      <span className="text-muted" style={{ fontSize: '0.70rem' }}>
                        {resolutionMessage.length} caracteres
                      </span>
                    </div>
                    <textarea
                      className={`form-control form-control-sm flex-grow-1 ${!profanityValidation.isValid ? 'is-invalid' : ''}`}
                      rows={4}
                      value={resolutionMessage}
                      onChange={e => setResolutionMessage(e.target.value)}
                      placeholder="Escribe la solución específica para los clientes..."
                      style={{
                        backgroundColor: isDark ? '#0f172a' : '#ffffff',
                        borderColor: !profanityValidation.isValid
                          ? '#ef4444'
                          : (isDark ? '#334155' : '#cbd5e1'),
                        color: isDark ? '#f8fafc' : '#0f172a',
                        fontSize: '0.82rem',
                        lineHeight: '1.45',
                        minHeight: '100px'
                      }}
                      disabled={isCompleting}
                    />
                  </div>

                  {/* Cierre fijo */}
                  <div
                    className="p-2.5 rounded-2 border"
                    style={{
                      backgroundColor: isDark ? '#0b141a' : '#f8fafc',
                      borderColor: isDark ? '#1f2e3d' : '#e2e8f0',
                      fontSize: '0.78rem'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="text-muted fw-semibold" style={{ fontSize: '0.66rem', letterSpacing: '0.3px' }}>
                        <FontAwesomeIcon icon={faLock} className="me-1" />
                        CIERRE INSTITUCIONAL (FIJO)
                      </span>
                    </div>
                    <div className="fw-medium" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                      {suffixText}
                    </div>
                  </div>
                </div>
              </div>

              {/* COLUMNA DERECHA: VISTA PREVIA SIEMPRE VISIBLE */}
              <div className="col-12 col-md-6 d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="small fw-bold d-flex align-items-center gap-1.5" style={{ color: isDark ? '#f1f5f9' : '#1e293b', fontSize: '0.84rem' }}>
                    <FontAwesomeIcon icon={faMobileAlt} className="text-success" />
                    <span>Vista Previa en WhatsApp</span>
                  </span>
                  <span className="badge bg-success bg-opacity-10 text-success border border-success px-2 py-0.5" style={{ fontSize: '0.68rem' }}>
                    En Vivo
                  </span>
                </div>

                <div
                  className="p-3 rounded-3 border flex-grow-1 d-flex flex-column justify-content-center"
                  style={{
                    backgroundColor: isDark ? '#0b141a' : '#efeae2',
                    borderColor: isDark ? '#1e293b' : '#d1d7db',
                    backgroundImage: isDark
                      ? 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 0)'
                      : 'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 0)',
                    backgroundSize: '16px 16px',
                    minHeight: '260px'
                  }}
                >
                  {/* Burbuja WhatsApp */}
                  <div
                    className="p-3 rounded-3 shadow-sm position-relative"
                    style={{
                      maxWidth: '96%',
                      alignSelf: 'flex-start',
                      backgroundColor: isDark ? '#005c4b' : '#ffffff',
                      color: isDark ? '#e9edef' : '#111b21',
                      borderRadius: '8px 8px 8px 0px',
                      border: isDark ? '1px solid #005c4b' : '1px solid #e0e0e0',
                      fontSize: '0.83rem',
                      lineHeight: '1.45'
                    }}
                  >
                    {/* Encabezado remitente */}
                    <div
                      className="fw-bold mb-1.5"
                      style={{
                        fontSize: '0.73rem',
                        color: isDark ? '#25d366' : '#075e54'
                      }}
                    >
                      Soporte Akasia • WhatsApp
                    </div>

                    {/* Prefijo */}
                    <div className="mb-2" style={{ opacity: 0.95 }}>
                      {prefixText}
                    </div>

                    {/* Mensaje de resolución editable */}
                    <div className="mb-2 fw-semibold" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {resolutionMessage || '(Escribe el detalle de la resolución)'}
                    </div>

                    {/* Cierre */}
                    <div style={{ opacity: 0.95 }}>
                      {suffixText}
                    </div>

                    {/* Hora y Doble Check Azul */}
                    <div
                      className="d-flex justify-content-end align-items-center gap-1 mt-1.5"
                      style={{ fontSize: '0.66rem', color: isDark ? '#8696a0' : '#667781' }}
                    >
                      <span>{timeStr}</span>
                      <span style={{ color: '#53bdeb', fontWeight: 'bold' }}>✓✓</span>
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-center">
                  <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                    📱 Así es exactamente como cada cliente vinculado recibirá el mensaje en su WhatsApp.
                  </small>
                </div>
              </div>
            </div>
          </div>

          {/* Footer del modal */}
          <div
            className="modal-footer border-top px-4 py-2.5 d-flex align-items-center justify-content-between"
            style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
          >
            <div className="small text-muted" style={{ fontSize: '0.76rem' }}>
              Al confirmar, los <strong>{totalTickets}</strong> ticket(s) vinculados se cerrarán con estatus <strong>Resuelto</strong>.
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-pill px-3 shadow-none"
                onClick={onClose}
                disabled={isCompleting}
                style={{ fontSize: '0.82rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm btn-success rounded-pill px-4 shadow-none fw-semibold d-flex align-items-center gap-1.5"
                onClick={() => {
                  if (!profanityValidation.isValid || !resolutionMessage.trim()) return;
                  onConfirm(resolutionMessage.trim());
                }}
                disabled={isCompleting || !resolutionMessage.trim() || !profanityValidation.isValid}
                style={{ backgroundColor: '#10b981', borderColor: '#10b981', fontSize: '0.82rem' }}
              >
                {isCompleting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    <span>Resolviendo y notificando...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Confirmar y Resolver</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagCompleteModal;
